import _ from 'lodash'
import {
  CellEditingStoppedEvent,
  CellKeyDownEvent,
  EditableCallbackParams,
  ProcessCellForExportParams,
  ValueGetterParams,
  ValueSetterParams,
  GridOptions,
} from 'ag-grid-community'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import {
  ColumnType,
  columnTypes,
  frameworkComponents,
  processDataFromClipboard,
} from '../../../containers/commons/AgGrid'
import { DEFAULT_COLUMN_DEF } from '../../../page-properties/bulksheet-properties'
import { intl } from '../../../../i18n'
import { useCallback, useMemo } from 'react'
import { useColumnDefs, sprintBasedComparator } from './columnDefs'
import { WbsItemSelectColumnOptionType } from '../../../hooks/useWbsItemSelectColumnOptions'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  isSprintRow,
} from '../productBacklog'
import { ProductBacklogNameCellRenderer } from '../components/ProductBacklogNameCellRenderer'
import {
  CustomEnumValue,
  FunctionProperty,
  PropertyType,
} from '../../../../lib/commons/appFunction'
import BoolExpression from '../../../../utils/boolExpression'
import TextCell from '../../../containers/commons/AgGrid/components/cell/common/richText/TextCell'
import { ClientSideTextFilter } from '../../../containers/BulkSheetView/components/filter'
import { requireSave } from '../../../../store/requiredSaveData'
import store from '../../../../store'
import { normalize } from '../../../../utils/multilineText'
import { TAG_DELIMITER } from '../../../../lib/functions/tag'
import { getLabel } from '../../../../lib/commons/i18nLabel'
import { dateVoService } from '../../../../domain/value-object/DateVO'
import { DISPLAY_DATE_FORMAT } from '../../../../utils/date'
import DateTimeVO from '../../../../domain/value-object/DateTimeVO'
import { SprintDetail } from '../../../../lib/functions/sprint'
import {
  processAutoCompleteCellFromClipboard,
  processMultiSelectNotSuctomEnumCellFromClipboard,
} from '../../../containers/BulkSheetView/gridOptions/cell/copyAndPaste'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'

export const useProductBacklogGridOptions = ({
  projectUuid,
  projectCode,
  reloadSingleRow,
  submitSingleRow,
  //   onChangeRowHeight,
  //   openWbsDetailDialog,
  //   closeWbsDetailDialog,
  selectColumnOptions,
  onClickedStatusCell,
  onClickSprintEstimates,
  incrementDataVersion,
  openSprintAdjustmentDialog,
}: {
  projectUuid: string
  projectCode: string
  reloadSingleRow?: ((uuid: string) => void) | undefined
  submitSingleRow?: (uuid: string) => Promise<void>
  //   onChangeRowHeight?: (value: number) => void
  //   openWbsDetailDialog?: () => void
  //   closeWbsDetailDialog?: () => void
  selectColumnOptions: WbsItemSelectColumnOptionType
  onClickedStatusCell?: (
    target: EventTarget | undefined,
    row: ProductBacklogItemRow | undefined
  ) => void
  onClickSprintEstimates?: (sprintId: string) => void
  incrementDataVersion?: () => void
  openSprintAdjustmentDialog?: (row: ProductBacklogItemRow) => void
}): GridOptions => {
  const columnDefs = useColumnDefs({
    projectUuid,
    projectCode,
    selectColumnOptions,
    onClickedStatusCell,
    incrementDataVersion,
    openSprintAdjustmentDialog,
  })

  // Name group column common def
  const onCloseWbsItemDetail = useCallback(
    (wbsItem: ProductBacklogItemRow) => {
      if (!reloadSingleRow || !wbsItem?.uuid) {
        return
      }
      return reloadSingleRow(wbsItem.uuid)
    },
    [reloadSingleRow]
  )
  const beforeOpenWbsItemDetail = useCallback(
    async (uuid: string) => {
      submitSingleRow && submitSingleRow(uuid)
      return true
    },
    [submitSingleRow]
  )

  const displayNameColCellRendererParams = useMemo(
    () => ({
      beforeOpenWbsItemDetail,
      onCloseWbsItemDetail,
    }),
    [beforeOpenWbsItemDetail, onCloseWbsItemDetail]
  )
  const displayNameUiMeta = useMemo(
    () =>
      ({
        propertyType: PropertyType.Text,
        maxLength: 256,
        requiredIf: BoolExpression.of(true),
      }) as Partial<FunctionProperty>,
    []
  )

  const gridOptions = useMemo(
    () => {
      return {
        // Grid context. Define grid specific properties
        context: {
          aggregateField: AggregateField.WBS_ITEM_WORKLOAD,
          ...selectColumnOptions,
          incrementDataVersion,
        },
        /**
         * contents below could be commonized.
         */
        // Styling
        groupHeaderHeight: 25,
        headerHeight: 45,
        floatingFiltersHeight: 25,
        rowHeight: ROW_HEIGHT.SMALL,
        treeData: true,
        excludeChildrenWhenTreeDataFiltering: true,
        autoGroupColumnDef: {
          field: 'wbsItem.displayName',
          headerName: intl.formatMessage({ id: 'projectPlan.displayName' }),
          width: 300,
          pinned: true,
          sortable: true,
          cellClass: 'sevend-ag-cell-project-plan-new-display-name-cell',
          comparator: sprintBasedComparator,
          cellRendererParams: {
            ...displayNameColCellRendererParams,
            suppressCount: true,
            suppressDoubleClickExpand: true,
            innerRenderer: ProductBacklogNameCellRenderer,
            innerRendererParams: {
              onClickSprintEstimates,
            },
            uiMeta: displayNameUiMeta,
          },
          editable: (params: EditableCallbackParams<ProductBacklogRow>) =>
            params.data && isProductBacklogItemRow(params.data),
          cellEditor: TextCell,
          cellEditorParams: {
            parseInitialValue: (value: string) => {
              return value || ''
            },
            uiMeta: displayNameUiMeta,
          },
          filter: ClientSideTextFilter,
          floatingFilter: true,
          valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
            if (!params.data) return undefined

            if (isSprintRow(params.data)) {
              return params.data.name
            } else if (isProductBacklogItemRow(params.data)) {
              return params.data.wbsItem.displayName
            }
            return undefined
          },
          valueSetter: (params: ValueSetterParams<ProductBacklogRow>) => {
            if (!params.data || isSprintRow(params.data)) return false
            if (
              (!params.oldValue && !params.newValue) ||
              params.oldValue === params.newValue
            ) {
              return false
            }
            params.data.edited = true
            params.data.wbsItem.displayName = params.newValue
            if (!params.data.editedData) {
              params.data.editedData = {
                ['wbsItem.displayName']: params.oldValue,
              }
            } else if (
              !params.data.editedData.hasOwnProperty('wbsItem.displayName')
            ) {
              params.data.editedData['wbsItem.displayName'] = params.oldValue
            }

            store.dispatch(requireSave())
            return true
          },
          cellClassRules: {
            'hover-over-can-drop': params => {
              return (
                params.context?.onTree &&
                params.context?.draggableNodeId &&
                params.node?.id === params.context.draggableNodeId
              )
            },
            'grid-edited-cell': params => {
              if (!params.data || !isProductBacklogItemRow(params.data)) {
                return false
              }
              const { editedData } = params.data
              if (!editedData) return false
              // グリッド上での名称編集、または新規追加行の場合に三角を表示
              const isDisplayNameEdited =
                Object.hasOwn(editedData, 'wbsItem.displayName') &&
                params.data.wbsItem.displayName !==
                  editedData['wbsItem.displayName']
              const isNewlyAddedRow = Object.hasOwn(
                editedData,
                `sprintChanged.${params.data.uuid}`
              )
              return isDisplayNameEdited || isNewlyAddedRow
            },
          },
          equals: (valA: string, valB: string) => valA === valB,
        },
        animateRows: true,
        groupDefaultExpanded: 0, // デフォルトは成果物は全折り畳み -> ui_stateで開いている行を制御
        suppressRowGroupHidesColumns: false,
        getDataPath: (data: ProductBacklogRow) => {
          return data.treeValue
        },
        postSortRows: (params: any) => {
          // ソート後にスプリント未設定を最後に移動
          const nodes = params.nodes
          if (!nodes || nodes.length === 0) return

          // スプリント未設定の行を抽出
          const unsetSprintNodes: any[] = []
          const otherNodes: any[] = []

          nodes.forEach((node: any) => {
            if (node.data && isSprintRow(node.data) && node.data.isUnset) {
              unsetSprintNodes.push(node)
            } else {
              otherNodes.push(node)
            }
          })

          // スプリント未設定を最後に配置
          if (unsetSprintNodes.length > 0) {
            params.nodes.length = 0
            params.nodes.push(...otherNodes, ...unsetSprintNodes)
          }
        },
        // Tooltip
        tooltipShowDelay: 100,
        // Default column definition for all columns
        defaultColDef: {
          ...DEFAULT_COLUMN_DEF,
          editable: (params: any) => {
            // SprintRowは編集不可、ProductBacklogItemRowは編集可能
            if (!params.data) return false
            return isProductBacklogItemRow(params.data)
          },
        },
        // Row actions
        rowDragManaged: false,
        rowDragMultiRow: true,
        suppressMoveWhenRowDragging: true,
        // ドラッグ可能な行を制限（ProductBacklogItemのみ）
        isRowDragAllowed: (params: any) => {
          return params.data && isProductBacklogItemRow(params.data)
        },
        enterNavigatesVerticallyAfterEdit: true,
        // セル編集完了後にフォーカスを確実に設定する
        // 同一成果物の複数行で共有配列を更新すると、ag-gridのフォーカス管理が乱れることがあるため
        onCellEditingStopped: (params: CellEditingStoppedEvent) => {
          // Enterキーで下に移動した場合、明示的にフォーカスを設定し直す
          // 同一成果物の複数行で共有配列を更新するとフォーカス管理が乱れるため
          const targetFields = [
            'wbsItem.estimatedWorkload.perSprint', // 1スプリント当たり
            'wbsItem.estimatedWorkload', // 見積成果物
          ]
          if (targetFields.includes(params.colDef.field || '')) {
            const focusedCell = params.api.getFocusedCell()
            if (focusedCell) {
              // 非同期でフォーカスを再設定（ag-gridの内部処理完了後に実行）
              setTimeout(() => {
                params.api.setFocusedCell(
                  focusedCell.rowIndex,
                  focusedCell.column
                )
              }, 0)
            }
          }
        },
        onCellKeyDown: (params: CellKeyDownEvent) => {
          // @ts-ignore
          if (!['Delete', 'Backspace'].includes(params.event.key)) return

          // Do not clear while editing cells,
          // because you will not working undo if you erase text during editing.
          const editingCells = params.api.getEditingCells()
          if (!_.isEmpty(editingCells)) return

          const cellRanges = params.api.getCellRanges() || []
          cellRanges.forEach(range => {
            const start = Math.min(
              range.startRow!.rowIndex,
              range.endRow!.rowIndex
            )
            const end = Math.max(
              range.startRow!.rowIndex,
              range.endRow!.rowIndex
            )
            range.columns.forEach(column => {
              const colDef = column.getColDef()
              if (
                typeof colDef.editable === 'function'
                  ? colDef.editable(params)
                  : colDef.editable
              ) {
                for (let i = start; i <= end; i++) {
                  const rowNode = params.api.getDisplayedRowAtIndex(i)
                  rowNode?.setDataValue(column, undefined)
                }
              }
            })
          })
        },
        // Column definition
        columnTypes: columnTypes(),
        components: frameworkComponents,
        // cellClassRules: PRODUCT_BACKLOG_CELL_CLASS_RULES,
        columnDefs,
        getRowStyle: params => {
          if (isSprintRow(params.data)) {
            return { fontWeight: 'bold' }
          }
        },
        // Copy & paste
        processCellForClipboard,
        processCellFromClipboard,
        processDataFromClipboard,
        // Footer
        statusBar: {
          statusPanels: [
            {
              statusPanel: 'agTotalAndFilteredRowCountComponent',
              align: 'left',
            },
            {
              statusPanel: 'agAggregationComponent',
              statusPanelParams: {
                aggFuncs: ['sum', 'count'],
              },
              align: 'left',
            },
          ],
        },
        localeText: {
          sum: intl.formatMessage({
            id: 'bulksheet.statusPanel.sum',
          }),
          count: intl.formatMessage({
            id: 'bulksheet.statusPanel.count',
          }),
          totalAndFilteredRows: intl.formatMessage({
            id: 'bulksheet.statusPanel.totalAndFilteredRows.title',
          }),
          of: intl.formatMessage({
            id: 'bulksheet.statusPanel.totalAndFilteredRows.division',
          }),
        },
        // excelStyles: excelStyles,
        processUnpinnedColumns: () => [],
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  return gridOptions as GridOptions
}

export const processCellForClipboard = ({
  column,
  value,
  node,
  context,
}: ProcessCellForExportParams) => {
  const colDef = column.getColDef()
  const { field, type, cellEditorParams } = colDef
  if (!value) return value
  if (['action', 'deliverableAttachmentSummary'].includes(field!)) {
    return ''
  }
  if (field === 'wbsItem.type') {
    return value?.name
  }
  if (field === 'wbsItem.ticketType') {
    const wbsItemType = node?.data?.body?.wbsItem.wbsItemType
    return wbsItemType?.isTicket() ? wbsItemType?.name : ''
  }
  if (field === 'wbsItem.description') {
    return `"${normalize(value ?? '')}"`
  }
  if (field === 'commentSummary.latestComment') {
    return value?.text
  }
  if (field === 'wbsItem.watchers') {
    return value?.map(v => v.name).join(',')
  }
  if (field === 'wbsItem.tags') {
    return value?.map(v => v.name).join(TAG_DELIMITER)
  }
  if (!type) {
    if (typeof value === 'object') {
      return value.name ?? value.displayName
    }
    return value
  }
  if (type.includes(ColumnType.autocomplete)) {
    return (
      getLabel(value?.nameI18n) ||
      value?.name ||
      value?.displayName ||
      value?.officialName
    )
  }
  if (type.includes(ColumnType.customEnum)) {
    const { customEnumCode } = cellEditorParams
    const options: CustomEnumValue[] = context[customEnumCode] ?? []
    const option = options.find(v => v.value === value)
    return getLabel(option?.nameI18n) || option?.name || ''
  }
  if (type.includes(ColumnType.date)) {
    const dateVO = dateVoService.construct(value)
    return dateVoService.isValid(dateVO)
      ? dateVoService.format(dateVO, DISPLAY_DATE_FORMAT)
      : ''
  }
  if (type.includes(ColumnType.dateTime)) {
    return new DateTimeVO(value).toString()
  }
  if (type.includes(ColumnType.multiLineText)) {
    return `"${normalize(value ?? '')}"`
  }
  return value
}

export const processCellFromClipboard = ({
  column,
  value,
  context,
  node,
  api,
}: ProcessCellForExportParams) => {
  const colDef = column.getColDef()
  const { field, type, cellEditorParams } = colDef
  if (!value) return value
  if (field === 'wbsItem.watchers') {
    const names = value.split(',').filter(v => !!v)
    const options = context['member']
    return options.filter(o => names.includes(o.name))
  }
  if (field === 'wbsItem.sprint') {
    const team = node?.data?.body?.wbsItem.team
    if (!team) return undefined
    return context.sprint?.find(
      (v: SprintDetail) => v.teamUuid === team.uuid && v.name === value
    )
  }
  if (field === 'wbsItem.tags') {
    const names = value.split(TAG_DELIMITER).filter(v => !!v)
    const options = context['tag'] ?? []
    return options.filter(v => names.includes(v.name))
  }
  if (!type) return value
  const retVal = processAutoCompleteCellFromClipboard(
    column,
    node,
    value,
    context
  )
  if (!!retVal) return retVal
  if (type.includes(ColumnType.customEnum)) {
    const options: CustomEnumValue[] =
      context[cellEditorParams.customEnumCode] ?? []
    const target = options.find(o =>
      [getLabel(o.nameI18n), o.name].filter(v => !!v).includes(value)
    )
    return target?.value
  }
  // TODO: Store date data in VO instead of string
  // Because the scope of the impact is large and the details of the fix have not yet been decided, we are implementing string conversion as a temporary measure.
  if (type.includes(ColumnType.date)) {
    const dateVO = dateVoService.construct(value)
    return dateVoService.isValid(dateVO) ? dateVoService.format(dateVO) : ''
  }
  if (type.includes(ColumnType.dateTime)) {
    return new DateTimeVO(value).serialize()
  }
  const multiSelectValue = processMultiSelectNotSuctomEnumCellFromClipboard(
    column,
    value
  )
  if (!!multiSelectValue) return multiSelectValue
  return value
}
